import { useTranslation } from 'react-i18next';
import { CAT_META } from '../../utils/mapConstants';
import type { VenueCategory, Venue } from '../../types';
import { Icon, MapPin } from '../ui/Icon';
import type { ReactNode } from 'react';
import MapFilterBar from './MapFilterBar';

interface Props {
  /** Chips before the rest — the destination chips (`LegChips`). */
  leading?: ReactNode;
  venues: Venue[];
  /** Empty = no category filter, every category shows. */
  selectedCats: Set<VenueCategory>;
  onToggleCat: (cat: VenueCategory) => void;
  onShowAll: () => void;
}

export default function VenueCategoryFilterBar({ leading, venues, selectedCats, onToggleCat, onShowAll }: Props) {
  const { t } = useTranslation();

  const usedCats = (Object.keys(CAT_META) as VenueCategory[])
    .filter(cat => venues.some(v => v.category === cat));
  if (usedCats.length === 0) return null;

  const noFilter = selectedCats.size === 0;

  return (
    <MapFilterBar>
      {leading}
      <button
        onClick={onShowAll}
        title={t('map.showAllCategories')}
        className={`flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-full border text-xs font-medium transition-all cursor-pointer select-none ${
          noFilter
            ? 'border-transparent bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-sm'
            : 'border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 bg-transparent opacity-50'
        }`}
      >
        <Icon as={MapPin} size={12} />
        {t('venues.filter.all')}
        <span className={`text-[10px] font-semibold ${noFilter ? 'opacity-75' : ''}`}>{venues.length}</span>
      </button>

      {usedCats.map(cat => {
        const meta = CAT_META[cat];
        const selected = selectedCats.has(cat);
        const shown = noFilter || selected;
        const count = venues.filter(v => v.category === cat).length;
        const catLabel = t(`venues.categories.${cat}` as Parameters<typeof t>[0]);
        return (
          <button
            key={cat}
            onClick={() => onToggleCat(cat)}
            title={selected ? t('map.removeCategoryFilter', { label: catLabel }) : t('map.showOnlyCategory', { label: catLabel })}
            className={`flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-full border text-xs font-medium transition-all cursor-pointer select-none ${
              shown
                ? 'border-transparent text-white shadow-sm'
                : 'border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 bg-transparent opacity-50'
            }`}
            style={shown ? { backgroundColor: meta.markerColor } : {}}
          >
            <Icon as={meta.Icon} size={12} />
            {catLabel}
            <span className={`text-[10px] font-semibold ${shown ? 'opacity-75' : ''}`}>{count}</span>
          </button>
        );
      })}
    </MapFilterBar>
  );
}
